import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Formik } from "formik";
import { Calendar, Plus, Trash2, AlertCircle } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import theme from "@utils/theme";

// 🎨 Theme Colors
const colors = {
  primary: theme.orange.orange500,
  primaryLight: theme.orange.orange100,
  primaryDark: theme.orange.orange600,
  error: "#ef4444",
  gray50: "#f8fafc",
  gray100: "#f1f5f9",
  gray200: "#e2e8f0",
  gray300: "#cbd5e1",
  gray400: "#94a3b8",
  gray500: "#64748b",
  gray600: "#475569",
  gray700: "#334155",
  gray800: "#1e293b",
  white: "#ffffff",
};

// Styled Components
const Container = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${colors.gray200};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${colors.primaryLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.gray800};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 13px;
  color: ${colors.gray500};
  margin: 2px 0 0 0;
`;

const ScheduleTable = styled.div`
  margin-top: 12px;
  border: 1px solid ${colors.gray200};
  border-radius: 8px;
  overflow: hidden;
`;

const ScheduleRow = styled.div<{ isClosed?: boolean }>`
  display: grid;
  grid-template-columns: 100px 1fr auto;
  align-items: start;
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.gray200};
  background: ${(props) => (props.isClosed ? colors.gray50 : colors.white)};
  transition: all 0.1s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) => (props.isClosed ? colors.gray50 : colors.gray50)};
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const DayLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray800};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DayStatus = styled.span<{ status: "open" | "closed" | "24h" }>`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;

  ${(props) => {
    switch (props.status) {
      case "open":
        return `
          background: ${colors.primaryLight};
          color: ${colors.primaryDark};
        `;
      case "closed":
        return `
          background: ${colors.gray200};
          color: ${colors.gray600};
        `;
      case "24h":
        return `
          background: ${colors.gray100};
          color: ${colors.gray700};
          border: 1px solid ${colors.gray300};
        `;
    }
  }}
`;

const TimeControls = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const TimeSlotGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const TimeSlotRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .react-datepicker-wrapper {
    width: auto;
  }

  .react-datepicker__input-container input {
    width: 65px;
    padding: 6px 4px;
    font-size: 12px;
    border: 1px solid ${colors.gray300};
    border-radius: 4px;
    text-align: center;
    cursor: pointer;
    background: ${colors.white};

    &:focus {
      outline: none;
      border-color: ${colors.primary};
    }
  }
`;

const TimeSlotError = styled.div`
  color: ${colors.error};
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
`;

const AddTimeButton = styled.button<{ disabled?: boolean }>`
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  color: ${(props) => (props.disabled ? colors.gray400 : colors.primary)};
  border: 1px dashed
    ${(props) => (props.disabled ? colors.gray300 : colors.gray400)};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    border-color: ${(props) =>
      props.disabled ? colors.gray300 : colors.primary};
    background: ${(props) =>
      props.disabled ? "transparent" : colors.primaryLight};
  }
`;

const RemoveTimeButton = styled.button`
  padding: 4px;
  background: ${colors.gray100};
  color: ${colors.gray500};
  border: 1px solid ${colors.gray300};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${colors.gray200};
    color: ${colors.gray700};
  }
`;

const DayActions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ variant: "primary" | "secondary" }>`
  padding: 5px 10px;
  border: 1px solid ${colors.gray300};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 50px;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: ${colors.primary};
          color: white;
          border-color: ${colors.primary};
          &:hover { 
            background: ${colors.primaryDark};
          }
        `;
      case "secondary":
        return `
          background: ${colors.white};
          color: ${colors.gray700};
          &:hover { 
            background: ${colors.gray50};
          }
        `;
    }
  }}
`;

// Validation functions
const validateTimeSlot = (
  timeSlot: any,
  allTimeSlots: any[],
  index: number,
) => {
  const errors: any = {};

  // Convert time strings to minutes for comparison
  const startMinutes = convertTimeToMinutes(timeSlot.startTime);
  const endMinutes = convertTimeToMinutes(timeSlot.endTime);

  // Check if start time is before end time
  if (startMinutes >= endMinutes) {
    errors.timeSlot = "End time must be after start time";
    return errors;
  }

  // Check for overlapping time slots
  for (let i = 0; i < allTimeSlots.length; i++) {
    if (i !== index) {
      const otherStart = convertTimeToMinutes(allTimeSlots[i].startTime);
      const otherEnd = convertTimeToMinutes(allTimeSlots[i].endTime);

      if (
        (startMinutes >= otherStart && startMinutes < otherEnd) ||
        (endMinutes > otherStart && endMinutes <= otherEnd) ||
        (startMinutes <= otherStart && endMinutes >= otherEnd)
      ) {
        errors.timeSlot = "Time slots cannot overlap";
        break;
      }
    }
  }

  return errors;
};

const convertTimeToMinutes = (timeString: string): number => {
  // Handle 12-hour format with AM/PM
  const timeMatch = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (timeMatch) {
    let [_, hoursStr, minutesStr, period] = timeMatch;
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (period.toUpperCase() === "PM" && hours !== 12) {
      hours += 12;
    } else if (period.toUpperCase() === "AM" && hours === 12) {
      hours = 0;
    }
    return hours * 60 + minutes;
  }

  // Fallback for 24-hour format
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
};

const canAddTimeSlot = (schedule: any) => {
  if (schedule.isClosed || schedule.is24Hours) return false;

  // Check if all existing time slots are valid
  const timeSlots = schedule.timeSlots || [];
  for (let i = 0; i < timeSlots.length; i++) {
    const errors = validateTimeSlot(timeSlots[i], timeSlots, i);
    if (errors.timeSlot) {
      return false;
    }
  }

  return true;
};

// Props interface
interface StoreAvailabilityProps {
  value?: any[];
  onChange?: (schedules: any[]) => void;
}

// Main Component
const StoreAvailability: React.FC<StoreAvailabilityProps> = ({
  value = [],
  onChange = () => {},
}) => {
  const [initialData, setInitialData] = useState(value);

  // ✅ keep in sync with parent when backend data or form changes
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      setInitialData(value);
    }
  }, [value]);

  const daysOfWeek = [
    { key: "sunday", label: "Sun" },
    { key: "monday", label: "Mon" },
    { key: "tuesday", label: "Tue" },
    { key: "wednesday", label: "Wed" },
    { key: "thursday", label: "Thu" },
    { key: "friday", label: "Fri" },
    { key: "saturday", label: "Sat" },
  ];

  const initialValues = {
    dailySchedules:
      value.length > 0
        ? value
        : daysOfWeek.map((day) => ({
            day: day.key,
            is24Hours: false,
            isClosed: false,
            timeSlots: [
              {
                id: `slot-${Date.now()}-${day.key}`,
                startTime: "09:00 AM",
                endTime: "05:00 PM",
              },
            ],
          })),
  };

  const handleTimeSlotChange = (
    dayKey: string,
    timeSlotId: string,
    field: string,
    value: string,
    values: any,
    setFieldValue: any,
  ) => {
    const updatedSchedules = values.dailySchedules.map((s: any) =>
      s.day === dayKey
        ? {
            ...s,
            timeSlots: (s.timeSlots || []).map((ts: any) =>
              ts.id === timeSlotId ? { ...ts, [field]: value } : ts,
            ),
          }
        : s,
    );

    setFieldValue("dailySchedules", updatedSchedules);
    onChange(updatedSchedules);
  };

  return (
    <Container>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={() => {}} // No submission needed, we use onChange
      >
        {({ values, setFieldValue }) => (
          <div>
            <SectionHeader>
              <SectionIcon>
                <Calendar size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>Store Hours</SectionTitle>
                <SectionDescription>
                  Set operating hours for each day of the week
                </SectionDescription>
              </div>
            </SectionHeader>

            <ScheduleTable>
              {daysOfWeek.map((day) => {
                const schedule =
                  values.dailySchedules.find((s: any) => s.day === day.key) ||
                  initialValues.dailySchedules[0];
                const status = schedule.isClosed
                  ? "closed"
                  : schedule.is24Hours
                    ? "24h"
                    : "open";

                return (
                  <ScheduleRow key={day.key} isClosed={schedule.isClosed}>
                    <DayLabel>
                      {day.label}.
                      <DayStatus status={status}>
                        {status === "closed"
                          ? "Closed"
                          : status === "24h"
                            ? "24h"
                            : "Open"}
                      </DayStatus>
                    </DayLabel>

                    <TimeControls>
                      {!schedule.isClosed && !schedule.is24Hours ? (
                        <TimeSlotGroup>
                          {(schedule.timeSlots || []).map(
                            (timeSlot: any, index: number) => {
                              const slotErrors = validateTimeSlot(
                                timeSlot,
                                schedule.timeSlots || [],
                                index,
                              );
                              const hasError = !!slotErrors.timeSlot;

                              return (
                                <div key={timeSlot.id}>
                                  <TimeSlotRow>
                                    <DatePicker
                                      selected={
                                        timeSlot.startTime
                                          ? (() => {
                                              // Handle "h:mm aa" format
                                              const timeMatch =
                                                timeSlot.startTime.match(
                                                  /(\d+):(\d+)\s*(AM|PM)/i,
                                                );
                                              if (timeMatch) {
                                                const date = new Date();
                                                let [
                                                  _,
                                                  hoursStr,
                                                  minutesStr,
                                                  period,
                                                ] = timeMatch;
                                                let hours = parseInt(
                                                  hoursStr,
                                                  10,
                                                );
                                                const minutes = parseInt(
                                                  minutesStr,
                                                  10,
                                                );

                                                if (
                                                  period.toUpperCase() ===
                                                    "PM" &&
                                                  hours !== 12
                                                ) {
                                                  hours += 12;
                                                } else if (
                                                  period.toUpperCase() ===
                                                    "AM" &&
                                                  hours === 12
                                                ) {
                                                  hours = 0;
                                                }

                                                date.setHours(
                                                  hours,
                                                  minutes,
                                                  0,
                                                  0,
                                                );
                                                return date;
                                              }
                                              // Fallback for 24h format
                                              return new Date(
                                                `2000-01-01T${timeSlot.startTime}`,
                                              );
                                            })()
                                          : null
                                      }
                                      onChange={(date: any) => {
                                        if (date) {
                                          // Format as "h:mm aa" directly
                                          let hours = date.getHours();
                                          const minutes = date
                                            .getMinutes()
                                            .toString()
                                            .padStart(2, "0");
                                          const ampm =
                                            hours >= 12 ? "PM" : "AM";
                                          hours = hours % 12;
                                          hours = hours ? hours : 12; // the hour '0' should be '12'
                                          const strTime = `${hours}:${minutes} ${ampm}`;

                                          handleTimeSlotChange(
                                            day.key,
                                            timeSlot.id,
                                            "startTime",
                                            strTime,
                                            values,
                                            setFieldValue,
                                          );
                                        }
                                      }}
                                      showTimeSelect
                                      showTimeSelectOnly
                                      timeIntervals={30}
                                      timeCaption="Time"
                                      dateFormat="h:mm aa"
                                    />
                                    <span style={{ fontSize: "12px" }}>to</span>
                                    <DatePicker
                                      selected={
                                        timeSlot.endTime
                                          ? (() => {
                                              // Handle "h:mm aa" format
                                              const timeMatch =
                                                timeSlot.endTime.match(
                                                  /(\d+):(\d+)\s*(AM|PM)/i,
                                                );
                                              if (timeMatch) {
                                                const date = new Date();
                                                let [
                                                  _,
                                                  hoursStr,
                                                  minutesStr,
                                                  period,
                                                ] = timeMatch;
                                                let hours = parseInt(
                                                  hoursStr,
                                                  10,
                                                );
                                                const minutes = parseInt(
                                                  minutesStr,
                                                  10,
                                                );

                                                if (
                                                  period.toUpperCase() ===
                                                    "PM" &&
                                                  hours !== 12
                                                ) {
                                                  hours += 12;
                                                } else if (
                                                  period.toUpperCase() ===
                                                    "AM" &&
                                                  hours === 12
                                                ) {
                                                  hours = 0;
                                                }

                                                date.setHours(
                                                  hours,
                                                  minutes,
                                                  0,
                                                  0,
                                                );
                                                return date;
                                              }
                                              // Fallback for 24h format
                                              return new Date(
                                                `2000-01-01T${timeSlot.endTime}`,
                                              );
                                            })()
                                          : null
                                      }
                                      onChange={(date: any) => {
                                        if (date) {
                                          // Format as "h:mm aa" directly
                                          let hours = date.getHours();
                                          const minutes = date
                                            .getMinutes()
                                            .toString()
                                            .padStart(2, "0");
                                          const ampm =
                                            hours >= 12 ? "PM" : "AM";
                                          hours = hours % 12;
                                          hours = hours ? hours : 12; // the hour '0' should be '12'
                                          const strTime = `${hours}:${minutes} ${ampm}`;

                                          handleTimeSlotChange(
                                            day.key,
                                            timeSlot.id,
                                            "endTime",
                                            strTime,
                                            values,
                                            setFieldValue,
                                          );
                                        }
                                      }}
                                      showTimeSelect
                                      showTimeSelectOnly
                                      timeIntervals={30}
                                      timeCaption="Time"
                                      dateFormat="h:mm aa"
                                    />
                                    <RemoveTimeButton
                                      type="button"
                                      onClick={() => {
                                        const updatedSchedules =
                                          values.dailySchedules.map((s: any) =>
                                            s.day === day.key
                                              ? {
                                                  ...s,
                                                  timeSlots: s.timeSlots.filter(
                                                    (ts: any) =>
                                                      ts.id !== timeSlot.id,
                                                  ),
                                                }
                                              : s,
                                          );
                                        setFieldValue(
                                          "dailySchedules",
                                          updatedSchedules,
                                        );
                                        onChange(updatedSchedules);
                                      }}
                                      disabled={
                                        (schedule.timeSlots || []).length <= 1
                                      }
                                    >
                                      <Trash2 size={10} />
                                    </RemoveTimeButton>
                                  </TimeSlotRow>
                                  {hasError && (
                                    <TimeSlotError>
                                      <AlertCircle size={10} />
                                      {slotErrors.timeSlot}
                                    </TimeSlotError>
                                  )}
                                </div>
                              );
                            },
                          )}
                          <AddTimeButton
                            type="button"
                            onClick={() => {
                              const newTimeSlot = {
                                id: `slot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                startTime: "09:00 AM",
                                endTime: "05:00 PM",
                              };
                              const updatedSchedules =
                                values.dailySchedules.map((s: any) =>
                                  s.day === day.key
                                    ? {
                                        ...s,
                                        timeSlots: [
                                          ...s.timeSlots,
                                          newTimeSlot,
                                        ],
                                      }
                                    : s,
                                );
                              setFieldValue("dailySchedules", updatedSchedules);
                              onChange(updatedSchedules);
                            }}
                            disabled={!canAddTimeSlot(schedule)}
                          >
                            <Plus size={10} />
                          </AddTimeButton>
                        </TimeSlotGroup>
                      ) : schedule.is24Hours ? (
                        <div
                          style={{
                            color: colors.gray500,
                            fontSize: "12px",
                            fontStyle: "italic",
                          }}
                        >
                          24 hours operation
                        </div>
                      ) : (
                        <div
                          style={{
                            color: colors.gray500,
                            fontSize: "12px",
                            fontStyle: "italic",
                          }}
                        >
                          Store closed
                        </div>
                      )}
                    </TimeControls>

                    <DayActions>
                      <ActionButton
                        type="button"
                        variant={schedule.isClosed ? "primary" : "secondary"}
                        onClick={() => {
                          const updatedSchedules = values.dailySchedules.map(
                            (s: any) =>
                              s.day === day.key
                                ? {
                                    ...s,
                                    isClosed: !s.isClosed,
                                    is24Hours: false,
                                  }
                                : s,
                          );
                          setFieldValue("dailySchedules", updatedSchedules);
                          onChange(updatedSchedules);
                        }}
                      >
                        {schedule.isClosed ? "Open" : "Close"}
                      </ActionButton>

                      {!schedule.isClosed && (
                        <ActionButton
                          type="button"
                          variant={schedule.is24Hours ? "primary" : "secondary"}
                          onClick={() => {
                            const updatedSchedules = values.dailySchedules.map(
                              (s: any) =>
                                s.day === day.key
                                  ? {
                                      ...s,
                                      is24Hours: !s.is24Hours,
                                      timeSlots: !s.is24Hours
                                        ? []
                                        : s.timeSlots,
                                    }
                                  : s,
                            );
                            setFieldValue("dailySchedules", updatedSchedules);
                            onChange(updatedSchedules);
                          }}
                        >
                          24h
                        </ActionButton>
                      )}
                    </DayActions>
                  </ScheduleRow>
                );
              })}
            </ScheduleTable>
          </div>
        )}
      </Formik>
    </Container>
  );
};

export default StoreAvailability;
